import {Subscription} from "rxjs";
import * as signalR from "@microsoft/signalr";
import {isPlatformBrowser, Location} from '@angular/common';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {AdCommentApiService, AdDetailApiService} from "@numizmat/services/core";
import {AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';

import {AdType} from "@numizmat/enumerations";
import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {LoaderComponent} from "@numizmat/components";
import {CurrentUserStateService} from "@numizmat/services/account";
import {AdCommentResponse, PublicAdDetailResponse} from "@numizmat/services-models/core";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";
import {AdCarouselComponent} from "@numizmat/pages/ad-detail-page/components/ad-carousel/ad-carousel.component";
import {SellerPanelComponent} from "@numizmat/pages/ad-detail-page/components/seller-panel/seller-panel.component";
import {AdTimePanelComponent} from "@numizmat/pages/ad-detail-page/components/ad-time-panel/ad-time-panel.component";
import {ActionsPanelComponent} from "@numizmat/pages/ad-detail-page/components/actions-panel/actions-panel.component";
import {AdStatusInfoComponent} from "@numizmat/pages/ad-detail-page/components/ad-status-info/ad-status-info.component";
import {
  AdPricingPanelComponent
} from "@numizmat/pages/ad-detail-page/components/ad-pricing-panel/ad-pricing-panel.component";
import {
  CommentsSectionComponent
} from "@numizmat/pages/ad-detail-page/components/comments-section/comments-section.component";
import {
  DescriptionSectionComponent
} from "@numizmat/pages/ad-detail-page/components/description-section/description-section.component";
import {
  AdHubService,
  DEFAULT_WEBSITE_IMAGE_HEIGHT,
  DEFAULT_WEBSITE_IMAGE_URL,
  DEFAULT_WEBSITE_IMAGE_WIDTH,
  JsonLdService,
  MetaTagService,
  ScrollerService
} from "@numizmat/services/common";
import {
  AdOfferSectionComponent
} from "@numizmat/pages/ad-detail-page/components/ad-offer-section/ad-offer-section.component";
import {
  LastVisitedAdsSectionComponent
} from "@numizmat/pages/ad-detail-page/components/last-visited-ads-section/last-visited-ads-section.component";

@Component({
    selector: 'nz-ad-detail-page',
    imports: [
        RouterLink,
        LoaderComponent,
        AdStatusInfoComponent,
        AdPricingPanelComponent,
        AdTimePanelComponent,
        AdCarouselComponent,
        SellerPanelComponent,
        ActionsPanelComponent,
        DescriptionSectionComponent,
        CommentsSectionComponent,
        AdOfferSectionComponent,
        LastVisitedAdsSectionComponent
    ],
    templateUrl: './ad-detail-page.component.html',
    styleUrl: './ad-detail-page.component.scss'
})
export class AdDetailPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId: Object = inject(PLATFORM_ID);

  private router: Router = inject(Router);
  private location: Location = inject(Location)
  private adHubService: AdHubService = inject(AdHubService);
  private jsonLdService: JsonLdService = inject(JsonLdService);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private scrollerService: ScrollerService = inject(ScrollerService);
  private adDetailApiService: AdDetailApiService = inject(AdDetailApiService);
  private adCommentApiService: AdCommentApiService = inject(AdCommentApiService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  private subs: Subscription = new Subscription();

  protected adId!: string;
  protected loading: boolean = true;
  protected isUserLoggedIn: boolean = false;
  protected fullscreenOverlay: boolean = false;
  protected adDetails!: PublicAdDetailResponse;
  protected adComments: AdCommentResponse[] = [];

  protected readonly AdType = AdType;
  protected readonly appRoutes = appRoutes;

  @HostListener('window:beforeunload', ['$event'])
  async handleBeforeUnload(event: Event): Promise<void> {
    event.stopImmediatePropagation();
    try {
      await this.adHubService.removeFromGroup();
      this.adHubService.offReceiveNotification();
    } catch (_) {
    }
  }

  public async ngOnInit(): Promise<void> {
    const adId = this.activatedRoute.snapshot.paramMap.get('id');
    if (!!adId) {
      this.adId = adId;
      this.getAdDetails();
      this.getLoginState();
      this.getCurrentUser();
      this.getAllAdComments();
      this.createRouteChangeSubscription();
      this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.ad}/${this.adId}`);
      if (isPlatformBrowser(this.platformId)) {
        if (!this.adHubService.hubConnection || this.adHubService.hubConnection.state !== signalR.HubConnectionState.Connected) {
          await this.adHubService.startConnection();
        }
        await this.addToHubGroup();
      }
    } else {
      this.router.navigate([appRoutes.home]).then();
    }
  }

  public ngAfterViewInit(): void {
    this.scrollerService.scrollToTop();
  }

  public async ngOnDestroy(): Promise<void> {
    this.subs.unsubscribe();
    this.jsonLdService.removeSchema();
    this.metaTagService.updateTag({property: 'og:image', content: DEFAULT_WEBSITE_IMAGE_URL});
    this.metaTagService.updateTag({name: 'twitter:image', content: DEFAULT_WEBSITE_IMAGE_URL});
    this.metaTagService.updateTag({property: 'og:image:width', content: DEFAULT_WEBSITE_IMAGE_WIDTH});
    this.metaTagService.updateTag({property: 'og:image:height', content: DEFAULT_WEBSITE_IMAGE_HEIGHT});
    this.adHubService.offReceiveNotification();
    await this.adHubService.removeFromGroup();
  }

  protected goBack(): void {
    this.location.back();
  }

  private getAdDetails(): void {
    this.adDetailApiService.getPublicAdDetails(this.adId).subscribe({
      next: response => {
        this.loading = false;
        this.adDetails = response;
        this.adDetailStateService.setPublicAdDetails(response);
        this.setSchema();
        this.setMetaTags();
      }
    });
  }

  private getAllAdComments(): void {
    this.adCommentApiService.getAllAdComments(this.adId).subscribe({
      next: response => {
        this.adComments = response;
      }
    });
  }

  private getLoginState(): void {
    const subscription: Subscription = this.currentUserStateService.getLoginState().subscribe({
      next: (loginState: boolean) => {
        if (loginState) {
          this.getAdDetails();
        }
      }
    });
    this.subs.add(subscription);
  }

  private getCurrentUser(): void {
    const subscription: Subscription = this.currentUserStateService.getCurrentUser().subscribe({
      next: currentUser => {
        this.isUserLoggedIn = currentUser.loggedIn;
      }
    });
    this.subs.add(subscription);
  }

  private createRouteChangeSubscription(): void {
    const subscription: Subscription = this.activatedRoute.paramMap.subscribe({
      next: (params) => {
        const adId: string | null = params.get('id');
        if (!!adId) {
          if (adId !== this.adId) {
            this.onReload(adId).then();
          }
        } else {
          this.router.navigate([appRoutes.home]).then();
        }
      }
    });
    this.subs.add(subscription);
  }

  private async addToHubGroup(): Promise<void> {
    await this.adHubService.addToGroup(this.adId);
    this.adHubService.onReceiveNotification((data: any) => {
      this.fullscreenOverlay = data.isReloading;
      if (this.fullscreenOverlay) {
        setTimeout(() => {
          this.fullscreenOverlay = false;
        }, 800);
      }
      this.getAdDetails();
    });
  }

  private async onReload(adId: string): Promise<void> {
    this.adId = adId;
    this.loading = true;
    this.getAdDetails();
    this.adHubService.offReceiveNotification();
    await this.adHubService.removeFromGroup();
    await this.addToHubGroup();
  }

  private setSchema(): void {
    const adSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": this.adDetails.title,
      "image": this.adDetails.imageIds.map(id =>
        `${environment.apiImageAdUrl}/additional/${this.adDetails.adId}/${id}`
      ),
      "description": this.adDetails.description ?? this.adDetails.adSubcategoryName,
      "sku": this.adDetails.adId,
      "brand": {
        "@type": "Brand",
        "name": "Numizmat"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": 5,
        "bestRating": 5,
        "ratingCount": 100
      },
      "review": [
        {
          "@type": "Review",
          "author": {"@type": "Person", "name": "Системен потребител"},
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": 5
          }
        }
      ],
      "offers": {
        "@type": "Offer",
        "price": this.adDetails.price,
        "priceCurrency": "BGN",
        "availability": "https://schema.org/InStock",
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "name": "Връщане не е разрешено",
          "applicableCountry": "BG",
          "returnPolicyCategory": "https://schema.org/MerchantReturnNotPermitted"
        },
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "BG"
          },
          "shippingRate": {
            "@type": "MonetaryAmount",
            "currency": "BGN",
            "value": 0
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 2,
              "maxValue": 5,
              "unitCode": "DAY"
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 2,
              "maxValue": 5,
              "unitCode": "DAY"
            }
          },
          "description": "Съгласно общите условия на сайта и условията посочени от продавача."
        },
        "priceValidUntil": this.adDetails.endDateTime,
        "seller": {
          "@type": "Organization",
          "name": "Collectoria.bg"
        }
      }
    };
    this.jsonLdService.setSchema(adSchema);
  }

  private setMetaTags(): void {
    this.metaTagService.setTitle(`${this.adDetails.title} | Collectoria`);
    if (!!this.adDetails.description) {
      this.metaTagService.setDescription(`${this.adDetails.description} | Collectoria`);
    } else {
      this.metaTagService.setDescription(`${this.adDetails.adCategoryName} | ${this.adDetails.adSubcategoryName} | Collectoria`);
    }

    const imageUrl = `${environment.apiImageAdUrl}/additional/${this.adDetails.adId}/${this.adDetails.imageIds[0]}`;
    this.metaTagService.updateTag({property: 'og:image', content: imageUrl});
    this.metaTagService.updateTag({name: 'twitter:image', content: imageUrl});

    this.metaTagService.removeTag('property', 'og:image:width');
    this.metaTagService.removeTag('property', 'og:image:height');
  }
}
